import Link from "next/link"
import Footer from "../components/footer"
import Menu from "../components/menu"
import { useRef, useState } from "react"
import database from "../database.mjs"
import { Popover } from "react-tiny-popover"
import { NextSeo } from "next-seo"
import Map, { Marker, NavigationControl } from "react-map-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import YouTube from "react-youtube"

export async function getServerSideProps() {
  const properties = database
    .prepare(
      "select id, address, area, beds, baths, price, long, lat from properties_to_let where area = 'Barrow Upon Soar' order by timestamp desc limit 16"
    )
    .all()

  for (const property of properties) {
    const id = property?.["id"]
    const filename = database.prepare("select filename from properties_to_let_photos where property_id = ? limit 1").get(id)?.[
      "filename"
    ]

    property["thumbnail"] = `/api/images/${filename}.webp`
  }

  return {
    props: {
      properties,
    },
  }
}

const sellpoints = ["Top 5 UK Family-Friendly Locations", "Sought After Village in Leicestershire"]

function Barrow({ properties }) {
  const carouselRef = useRef()

  const [isVideoOpen, setIsVideoOpen] = useState(false)
  const [carouselIndex, setCarouselIndex] = useState(0)

  const [showRentPopover, setShowRentPopover] = useState(false)

  const mapRef = useRef()

  const desc =
    "At Huntleys, our experienced lettings agents are dedicated to our local area. We have effective marketing strategies that generate interest in your property. With a strong presence on the high street, eye-catching signage, and advertisements on popular property websites like Rightmove and Zoopla, we give your property the best chance of attracting the right tenant. Our top priority is providing excellent customer service and bringing people and properties together."

  return (
    <>
      <NextSeo title="Huntleys Estate Agents in Barrow upon Soar" description={desc} />
      <div className="h-[62.5vh] bg-cpink relative">
        <img className="object-cover w-full h-full select-none" src="/images/barrow.webp" alt="Barrow upon Soar" />
        <div className="absolute p-4 top-0 bottom-0 left-0 right-0 flex items-center justify-center bg-[rgba(0,0,0,.25)]">
          <div className="text-center">
            <h1 className="font-serif text-5xl font-semibold leading-tight text-center text-white md:text-6xl drop-shadow-xl">
              Letting Agents in Barrow upon Soar
            </h1>
            <h2 className="mt-4 text-xl font-medium text-white drop-shadow-sm">Properties for rent in LE11 and LE12</h2>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center w-full">
        <div className="flex items-center w-full sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
          <div className="flex px-4 py-12">
            <div className="text-stone-900">
              <div className="flex flex-col gap-x-12 lg:flex-row">
                <div>
                  <h2 className="font-serif text-2xl font-semibold">
                    Discover the Charms of Barrow upon Soar: A Serene Village with Community Spirit
                  </h2>
                  <div className="text-[15px] leading-[1.5] text-justify text-stone-800 font-light">
                    <p className="mt-6">
                      Barrow upon Soar is a picturesque village known for its close-knit community and serene atmosphere. With
                      six delightful local pubs, it truly captures the essence of village life. Nature enthusiasts can enjoy the
                      village's sculpture trail or take a step back in time by riding a steam train on the Great Central
                      Railway. The Barrow upon Soar property market offers fantastic opportunities for those seeking a peaceful
                      countryside lifestyle with convenient access to nearby towns and cities such as Loughborough and
                      Leicester.
                    </p>
                    <p className="mt-4">
                      At Huntleys, our team of knowledgeable estate agents is committed to serving our local community. We
                      employ effective marketing techniques that generate interest in your property. With a prominent presence
                      on the high street, attention-grabbing signage, and advertisements on well-known property websites such as
                      Rightmove and Zoopla, we maximize the exposure of your property and increase the likelihood of finding the
                      perfect tenant. Our utmost focus is on delivering exceptional customer service and facilitating successful
                      connections between people and properties.
                    </p>
                  </div>
                  <div className={[`mt-8 relative flex w-full mx-auto rounded aspect-video`].join(" ")}>
                    <div className="w-full h-full overflow-hidden rounded">
                      <div className="absolute top-0 left-0 right-0 h-24 rounded-t opacity-75 bg-gradient-to-b from-stone-900 to-transparent"></div>
                      <p className="absolute text-white drop-shadow-md top-8 left-6 lg:top-12 lg:left-16">Let with Huntleys</p>
                      <div className="absolute bottom-0 left-0 right-0 h-32 rounded-b opacity-75 bg-gradient-to-t from-stone-900 to-transparent"></div>
                      <div className="absolute left-8 bottom-6 lg:bottom-12 lg:left-16">
                        <h1 className="font-serif text-2xl font-medium text-white sm:text-3xl drop-shadow lg:text-4xl">
                          Kyfer Leech
                        </h1>
                        <p className="text-white drop-shadow-md">Lettings Valuer</p>
                      </div>
                      <div className="absolute top-0 bottom-0 left-0 right-0 flex">
                        <button
                          className={[
                            `flex p-4 m-auto text-white transition-all duration-300 border-2 border-white rounded-full hover:text-teal-900 hover:bg-white md:p-6 drop-shadow md:border-4`,
                          ].join(" ")}
                          onClick={() => {
                            document.body.classList.add("overflow-hidden")
                            setIsVideoOpen(true)
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="w-5 h-5 m-auto md:h-6 md:w-6"
                          >
                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                          </svg>
                        </button>
                      </div>
                      <img className="object-cover w-full h-full rounded select-none" src="/images/let-thumbnail.webp" />
                    </div>
                  </div>
                </div>
                <div className="text-[15px] mt-5 flex flex-col w-full lg:mt-0 lg:max-w-xs text-stone-800 min-w-[20rem]">
                  <div className="hidden w-full pt-5 border-t border-stone-900">
                    <p className="text-lg font-medium">£340k</p>
                    {/* <p className="mt-0">Average House Price in Barrow upon Soar</p> */}
                    <Popover
                      isOpen={showRentPopover}
                      positions={["bottom", "right"]}
                      content={
                        <div className="px-4 py-2">
                          <div className="px-3 py-2 bg-white rounded shadow-2xl text-stone-900">Huntleys Data 2023</div>
                        </div>
                      }
                    >
                      <div
                        onMouseEnter={() => setShowRentPopover(true)}
                        onMouseLeave={() => setShowRentPopover(false)}
                        className="flex items-center gap-x-1 hover:cursor-help"
                      >
                        <p className="mt-0">Average House Price in Barrow upon Soar</p>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-4 h-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                          />
                        </svg>
                      </div>
                    </Popover>
                  </div>
                  {sellpoints?.length > 0 && (
                    <div className="hidden grid-cols-2 mt-0 lg:grid lg:grid-cols-1 gap-y-2 border-stone-800">
                      {sellpoints?.map((_, index) => {
                        return (
                          <div key={index} className="flex items-center flex-1 gap-x-2">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                              <path
                                fillRule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <p>{sellpoints?.[index]?.replaceAll("&#42;", "")}</p>
                          </div>
                        )
                      })}
                    </div>
                  )}
                  <div className="flex flex-col w-full mt-5 lg:max-w-xs gap-y-3">
                    <Link
                      className="flex items-center justify-center flex-1 p-3 font-medium text-center transition-all duration-300 border rounded hover:bg-cpink-50 bg-cpink border-cpink hover:text-stone-900 hover:border-cpink-50 text-stone-900"
                      href="tel:+4401509320320"
                    >
                      01509 320 320
                    </Link>
                    {/* <Link
                      className="flex items-center justify-center flex-1 p-3 font-medium text-center transition-all duration-300 border rounded hover:bg-cpink-50 bg-cpink border-cpink hover:text-stone-900 hover:border-cpink-50 text-stone-900"
                      href="/buy"
                      target="_blank"
                    >
                      Properties for Sale
                    </Link> */}
                    <Link
                      className="flex items-center justify-center flex-1 p-3 font-medium text-center transition-all duration-300 border rounded hover:bg-cpink-50 bg-cpink border-cpink hover:text-stone-900 hover:border-cpink-50 text-stone-900"
                      href="/rent"
                      target="_blank"
                    >
                      Properties for Rent
                    </Link>
                    <Link
                      className="flex items-center justify-center flex-1 p-3 font-medium text-center transition-all duration-300 border rounded hover:bg-cpink-50 bg-cpink border-cpink hover:text-stone-900 hover:border-cpink-50 text-stone-900"
                      href="/valuation?type=sell"
                      target="_blank"
                    >
                      Arrange Valuation
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {properties?.length >= 1 && (
        <h1
          className="z-30 mx-4 font-serif text-3xl font-bold text-center text-stone-900 sm:text-5xl"
          style={{ lineHeight: 1.125 }}
        >
          Properties for sale in Hathern
        </h1>
      )}
      {properties?.length >= 4 && (
        <div className="pb-8 mt-12 bg-white">
          <h1
            className="z-30 mx-4 font-serif text-3xl font-bold text-center text-stone-900 sm:text-5xl"
            style={{ lineHeight: 1.125 }}
          >
            Properties in Barrow upon Soar
          </h1>
          <div className="relative px-4 mt-12">
            <div
              className="flex justify-between mx-auto overflow-x-hidden transition-all duration-300 gap-x-6 sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl no-scrollbar"
              ref={carouselRef}
            >
              {properties.map((property, index) => {
                return (
                  <Link
                    key={index}
                    className="snap-start relative block min-w-full overflow-hidden transition-all duration-300 rounded md:w-1/2 md:min-w-[calc(50%-0.5rem)] bg-stone-900 lg:max-w-1/3 lg:min-w-[calc(33.33%-1rem)] xl:max-w-1/4 xl:min-w-[calc(25%-1.125rem)] scroll-smooth shadow-sm"
                    href={`/rent/${property?.id}`}
                  >
                    <div className="relative top-0 bottom-0 left-0 right-0 w-full h-full">
                      <div className="aspect-[1.625] w-full overflow-hidden relative">
                        <img
                          alt={`${property?.address} is for sale in ${property?.area}`}
                          className="relative object-cover w-full h-full transition-all duration-500 select-none hover:scale-105"
                          src={property?.thumbnail}
                        />
                        {property?.is_sold && (
                          <div
                            className="absolute flex items-center justify-center p-2 overflow-hidden rounded-full pointer-events-none bottom-4 right-4 bg-stone-50 aspect-square text-stone-900 border-neutral-900"
                            // style={{ filter: "drop-shadow(0.5px 0.5px 0.75px rgba(0, 0, 0, 0.5))" }}
                          >
                            <p className="m-auto text-sm font-semibold">SOLD</p>
                          </div>
                        )}
                      </div>
                      <div className="absolute top-0 left-0 right-0 p-4 pointer-events-none">
                        <span className="inline-block px-3 py-2 font-medium tracking-wide text-white bg-opacity-75 rounded bg-stone-900 drop-shadow-xl">
                          {new Intl.NumberFormat("en-gb", {
                            style: "currency",
                            currency: "GBP",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(property?.price)}
                        </span>
                      </div>
                      <div className="z-20 flex justify-between p-4 bg-cpink text-stone-900">
                        <div>
                          <h1>{property?.address}</h1>
                          <p className="font-bold">{property?.area}</p>
                        </div>
                        <div className="flex text-center gap-x-4">
                          <div>
                            <h1 className="text-lg">{property?.beds}</h1>
                            <p className="-mt-1 text-sm">{property?.beds === 1 ? "bed" : "beds"}</p>
                          </div>
                          <div className="w-px bg-stone-900"></div>
                          <div>
                            <h1 className="text-lg">{property?.baths}</h1>
                            <p className="-mt-1 text-sm">{property?.beds === 1 ? "bath" : "baths"}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                )
              })}
              {properties?.length > 4 && (
                <div className="absolute top-0 bottom-0 left-0 right-0 flex pointer-events-none">
                  <div className="w-full h-full px-4">
                    <div className="relative w-full h-full mx-auto transition-all duration-300 m:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
                      <div className="absolute top-0 bottom-0 flex w-10 -left-3">
                        <div className="m-auto">
                          <button
                            aria-label="Left Scroll Carousel"
                            className={`block p-2 transition-all duration-300 bg-white border rounded-full pointer-events-auto drop-shadow ${
                              false
                                ? "cursor-not-allowed text-stone-400 border-stone-400"
                                : "cursor-pointer text-stone-900 border-stone-900 "
                            }`}
                            onClick={() => {
                              // if (carouselIndex === 0) return

                              setCarouselIndex(carouselIndex => Math.max(0, carouselIndex - 1))
                              carouselRef.current.scrollTo({
                                left: carouselRef.current.scrollLeft - carouselRef.current.clientWidth - 16,
                                behavior: "smooth",
                              })
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                              <path
                                fillRule="evenodd"
                                d="M11.03 3.97a.75.75 0 010 1.06l-6.22 6.22H21a.75.75 0 010 1.5H4.81l6.22 6.22a.75.75 0 11-1.06 1.06l-7.5-7.5a.75.75 0 010-1.06l7.5-7.5a.75.75 0 011.06 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="absolute top-0 bottom-0 flex w-10 -right-3">
                        <div className="m-auto">
                          <button
                            aria-label="Right Scroll Carousel"
                            onClick={() => {
                              // if (carouselIndex === properties.length - 1) return

                              setCarouselIndex(carouselIndex => carouselIndex + 1)
                              carouselRef.current.scrollTo({
                                left: carouselRef.current.scrollLeft + carouselRef?.current?.clientWidth + 16,
                                behavior: "smooth",
                              })
                            }}
                            className={`block p-2 transition-all duration-300 bg-white border rounded-full pointer-events-auto drop-shadow ${
                              false
                                ? "cursor-not-allowed text-stone-400 border-stone-400"
                                : "cursor-pointer text-stone-900 border-stone-900 "
                            }`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                              <path
                                fillRule="evenodd"
                                d="M12.97 3.97a.75.75 0 011.06 0l7.5 7.5a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 11-1.06-1.06l6.22-6.22H3a.75.75 0 010-1.5h16.19l-6.22-6.22a.75.75 0 010-1.06z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {properties?.length >= 1 && (
        <div className="mt-12">
          <div className="px-4">
            <div className="h-[62.5vh] sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl mx-auto transition-all duration-300 rounded overflow-hidden bg-cpink">
              <div id="map-container" className="relative w-full h-full overflow-hidden rounded lg:h-full">
                <Map
                  projection="globe"
                  reuseMaps
                  ref={mapRef}
                  scrollZoom={false}
                  mapboxAccessToken="pk.eyJ1IjoiaHVudGxleXMiLCJhIjoiY2xhY3AyNWR2MGMzcDN1bGRkaGlqdG14eiJ9.gAESyV1HhlLB1dz4hhF-tA"
                  initialViewState={{
                    latitude: 52.75248178474669,
                    longitude: -1.148830468726966,
                    zoom: 13,
                  }}
                  style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
                  mapStyle="mapbox://styles/huntleys/clacpu16j001s14od0751ssv0"
                >
                  <NavigationControl showCompass={false} />
                  {properties.map((property, index) => {
                    return (
                      <Marker
                        // onClick={() => {
                        //   window.location = `/buy/${property.id}`
                        // }}
                        key={property.id}
                        longitude={property.long}
                        latitude={property.lat}
                        anchor="bottom"
                      >
                        <Link href={`/rent/${property?.id}`}>
                          <img src="/images/marker-peach.svg" className="h-12 opacity-90 drop-shadow-sm hover:cursor-pointer" />
                        </Link>
                      </Marker>
                    )
                  })}
                </Map>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center w-full gap-6 pb-12 mt-10 sm:flex-row">
            {/* <Link href="/buy?area=Barrow upon Soar" prefetch={false}>
          <button
            aria-label="Properties For Sale"
            className="px-4 py-3 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 hover:bg-cpink-50 hover:border-cpink-50 bg-cpink border-cpink"
          >
            All Properties For Sale in Barrow upon Soar
          </button>
        </Link> */}
            <Link href="/rent?area=Barrow upon Soar" prefetch={false}>
              <button
                aria-label="Properties For Rent"
                className="px-4 py-3 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 hover:bg-cpink-50 hover:border-cpink-50 bg-cpink border-cpink"
              >
                All Properties For Rent in Barrow upon Soar
              </button>
            </Link>
          </div>
        </div>
      )}
      <section>
        <div className="relative flex bg-white">
          <div className="justify-center hidden w-full mx-auto max-h-64 sm:flex sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
            <div className="absolute bottom-0 left-0 right-0 flex top-12 bg-cpink"></div>
            <div className="z-10 items-center justify-center w-1/4 px-4 -mt-48 sm:-mt-0 sm:-mb-32 sm:w-2/5 md:-mb-48 lg:w-1/6 xl:w-2/7">
              <img alt="Phone" className="w-full select-none" src="/images/phone-3.png" />
              <img alt="Laptop" className="w-full select-none sm:hidden" src="/images/phone-3.png" />
            </div>
            <div className="z-10 flex w-3/5 pt-12 md:py-12 md:pt-[4.75rem]">
              <div className="px-4 my-auto">
                <h1 className="w-full font-serif text-4xl font-semibold text-stone-900 sm:text-left drop-shadow">
                  Find out what your property is worth
                </h1>
                <Link href="/valuation" target="_blank">
                  <button className="px-4 py-3 mt-6 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 bg-cpink-200 border-cpink-200 hover:bg-white hover:border-white">
                    Arrange a valuation
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white sm:hidden">
          <div className="flex justify-center px-4 sm:hidden">
            <img alt="Laptop" className="w-full max-w-sm select-none" src="/images/phone-3.png" />
          </div>
          <div className="relative px-4 -mt-96 bg-cpink">
            <div className="flex py-12 ">
              <div className="flex-1 min-w-0 sm:flex-2">
                <h1 className="w-full font-serif text-4xl font-semibold text-stone-900 sm:text-left drop-shadow">
                  Find out what your property is worth
                </h1>
                <Link href="/valuation" target="_blank">
                  <button className="px-4 py-3 mt-6 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 bg-cpink-200 border-cpink-200 hover:bg-white hover:border-white">
                    Arrange a valuation
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </section>
      <Menu />
      {isVideoOpen && (
        <div className={[`fixed z-50 top-0 bottom-0 left-0 right-0 flex flex-col w-screen h-screen bg-stone-900`].join(" ")}>
          <div className="flex justify-between w-full p-4 sm:px-4 text-stone-900 bg-cpink">
            <p className="text-md">Let with Huntleys</p>
            <button
              className="transition-all duration-300"
              onClick={() => {
                setIsVideoOpen(false)
                document.body.classList.remove("overflow-hidden")
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>
          <div className="w-full h-full">
            <YouTube
              videoId="XExeH6NGTD0"
              opts={{
                height: "100%",
                width: "100%",
                playerVars: {
                  autoplay: 1,
                  color: "white",
                },
              }}
              className="w-full h-full overflow-hidden"
            />
          </div>
        </div>
      )}
    </>
  )
}

export default Barrow

